import React from 'react';
import PropTypes from 'prop-types';
import Credits from '../base/Credits';
import IFrame from '../base/IFrame';
import Teasers from '../base/Teasers';
import Flourish from '../base/Flourish';
import Graphic from '../graphics/Graphic';
import AdPosition from '~components/base/AdPosition';
import Composite1 from '../images/Composite1';
import Composite2 from '../images/Composite2';
import Composite3 from '../images/Composite3';
import AudioDisclaimer from '../custom/AudioDisclaimer';
import AudioBlock from '../custom/AudioBlock';
import AudioVersion from '../custom/AudioVersion';
import VimeoBlock from '../custom/VimeoBlock';
import videoCap from '../../assets/images/video-screencap.jpg';
// uncomment following line and case below to use mapbox component
// import Mapbox from '../graphics/Mapbox';

const ArchieElement = ({
  data,
  relatedLinks,
  enableAudio,
  audio,
  audioData,
  updateAudioData,
  videoData,
  updateVideoData,
  windowWidth,
  windowHeight,
  playing,
  setPlaying,
  showAudioDisclaimer,
}) => {
  const archieObj = JSON.parse(data.archieJson);
  // check for archie key/values like this
  if (archieObj.archieTest) {
    console.log(`archieObj.archieTest key detected: ${archieObj.archieTest}`);
  }
  // case ads
  if (archieObj.ads) {
    return (
      <AdPosition positionId={archieObj.ads} overline />
    );
  }
  // case comments
  if (archieObj.comments) {
    const buttonText = archieObj.text ? archieObj.text : 'Comments: Join the discussion';
    return (
      <p className="discussion">
        <a href={archieObj.comments} className="benton-n6">{buttonText}</a>
      </p>
    );
  }
  // case teasers
  if (archieObj.teasers) {
    if (relatedLinks.length > 0) {
      return (<Teasers relatedlinks={relatedLinks} />);
    }
    return (null);
  }
  // case credits
  if (archieObj.credits) {
    return (<Credits data={archieObj} />);
  }

  if (archieObj.pubDate) {
    return null;
  }

  if (archieObj.graphic) {
    switch (archieObj.graphic) {
      case 'map': {
        return (<Map />);
      }
      default: {
        return null;
      }
    }
  }

  if (archieObj.composite) {
    switch (archieObj.composite) {
      case 'composite1': {
        return (<Composite1 data={archieObj} />);
      }
      case 'composite2': {
        return (<Composite2 data={archieObj} />);
      }
      case 'composite3': {
        return (<Composite3 data={archieObj} />);
      }
      default: {
        return null;
      }
    }
  }

  if (archieObj.audioDisclaimer) {
    if (showAudioDisclaimer.current) {
      return (
        <AudioDisclaimer
          data={archieObj}
          enableAudio={enableAudio}
          audio={audio}
        />
      );
    } return null;
  }

  if (archieObj.audio) {
    return (
      <AudioBlock
        data={archieObj}
        audioData={audioData}
        updateAudioData={updateAudioData}
        audio={audio}
        enableAudio={enableAudio}
        playing={playing}
        setPlaying={setPlaying}
      />
    );
  }

  if (archieObj.audioVersion) {
    return (
      <AudioVersion
        data={archieObj}
        playing={playing}
        setPlaying={setPlaying}
      />
    );
  }

  if (archieObj.videoId) {
    return (
      <VimeoBlock
        data={archieObj}
        windowWidth={windowWidth}
        windowHeight={windowHeight}
        videoData={videoData}
        updateVideoData={updateVideoData}
        audio={audio}
        enableAudio={enableAudio}
        playing={playing}
        setPlaying={setPlaying}
      />
    );
  }

  // case iframe
  if (archieObj.iframe) {
    return (
      <div className="iframe__wrapper">
        <IFrame className="related" data={archieObj} arcId={data._id} />
      </div>
    );
  }
  // case flourish
  if (archieObj.flourish) {
    return (<Flourish flourish={archieObj.flourish} />);
  }
  if (archieObj.graphic) {
    return (<Graphic data={archieObj} />);
  }
  if (archieObj.videoRefer) {
    return (
      <div className="related">
        <a href={archieObj.videoRefer} className="bentonsanscond-n6" target="_blank" rel="nofollow noreferrer">
          <img src={videoCap} alt={`Screen cap of a video of ${archieObj.videoHed}.`} />
          <p><span className="related--label bentonsanscond-n6">WATCH: </span>{archieObj.videoHed}</p>
        </a>
      </div>
    );
  }

  if (archieObj.discussion) {
    return (
      <div className="related">
        <p className="bentonsanscond-n6"><span className="related--label bentonsanscond-n6">JOIN THE DISCUSSION: </span><a href={archieObj.commentsLink} target="_blank" rel="nofollow noreferrer">Comment</a> on Kate’s story, or <a href={archieObj.discussionLink} target="_blank" rel="nofollow noreferrer">join us at a virtual event</a> to hear journalist Janelle Nanos discuss her reporting journey on Kate’s story.</p>
      </div>
    );
  }

  // UNCOMMENT TO USE MAPBOX COMPONENT
  // if (archieObj.mapbox) {
  //   return (<Mapbox />);
  // }

  return (
    <div className="archie content">
      <code>
        {JSON.stringify(archieObj)}
      </code>
    </div>
  );
};
ArchieElement.propTypes = {
  data: PropTypes.shape({
    content: PropTypes.string,
    type: PropTypes.oneOf(['custom_embed']),
    subtype: PropTypes.oneOf(['archieml']),
    archieJson: PropTypes.string,
    _id: PropTypes.string,
  }).isRequired,
  relatedLinks: PropTypes.arrayOf(
    PropTypes.shape({
      headlines: PropTypes.shape({
        basic: PropTypes.string,
      }),
      promoImage: PropTypes.shape({
        sizes: PropTypes.shape({
          _480: PropTypes.string,
        }),
        srcSet: PropTypes.string,
      }),
      url: PropTypes.string,
    }),
  ),
  enableAudio: PropTypes.func.isRequired,
  audio: PropTypes.bool.isRequired,
  audioData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      description: PropTypes.string,
      transcript: PropTypes.string,
    }),
  ).isRequired,
  updateAudioData: PropTypes.func.isRequired,
  videoData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      description: PropTypes.string,
      transcript: PropTypes.string,
    }),
  ).isRequired,
  updateVideoData: PropTypes.func.isRequired,
  windowWidth: PropTypes.number.isRequired,
  windowHeight: PropTypes.number.isRequired,
  playing: PropTypes.string.isRequired,
  setPlaying: PropTypes.func.isRequired,
  showAudioDisclaimer: PropTypes.shape().isRequired,
};

ArchieElement.defaultProps = {
  relatedLinks: null,
};

export default ArchieElement;
